import { useMemo, useState } from 'react'
import {
  Badge,
  BadgeStatus,
  Button,
  ConfirmDialog,
  DataTable,
  FilterBar,
  Input,
  KolomForm,
  Modal,
  PageHeader,
  Select,
  Textarea,
  FILTER_KOSONG,
  type KolomTabel,
  type NilaiFilter,
} from '@/components'
import { api, buatIdempotencyKey, pesanKesalahan } from '@/lib/api'
import { formatRupiah, formatTanggal } from '@/lib/format'
import type { AkunKas, TransferAntarunit } from '@/lib/types'
import { useApi, usePaged, useAksi } from '@/lib/useApi'
import { useOpsiUnit } from '@/lib/opsi'
import { useAuth } from '@/lib/auth'

const TINDAKAN = ['approve', 'send', 'receive', 'match'] as const
type Tindakan = (typeof TINDAKAN)[number]

export default function TransferAntarunitPage() {
  const { punyaIzin } = useAuth()
  const opsiUnit = useOpsiUnit()
  const aksi = useAksi()
  const [filter, setFilter] = useState<NilaiFilter>({ ...FILTER_KOSONG, dari: `${new Date().getFullYear()}-01-01` })
  const [statusFilter, setStatusFilter] = useState('')
  const [modal, setModal] = useState(false)
  const [konfirmasi, setKonfirmasi] = useState<{ tindakan: Tindakan; data: TransferAntarunit } | null>(null)

  const daftar = usePaged<TransferAntarunit>('/interunit-transfers', {
    query: {
      dari: filter.dari || undefined,
      sampai: filter.sampai || undefined,
      unitId: filter.unitId || undefined,
      status: statusFilter || undefined,
    },
  })

  const belumCocok = (t: TransferAntarunit) =>
    t.status !== 'MATCHED' && t.status !== 'CANCELLED' && t.status !== 'FAILED'

  const jalankan = async (id: string, tindakan: Tindakan) => {
    const hasil = await aksi.jalankan(
      () => api.post(`/interunit-transfers/${id}/${tindakan}`, {}, { idempotencyKey: buatIdempotencyKey() }),
      { sukses: `Transfer berhasil di-${tindakan}.` },
    )
    if (hasil !== null) {
      setKonfirmasi(null)
      daftar.muat()
    }
  }

  const kolom = useMemo<KolomTabel<TransferAntarunit>[]>(
    () => [
      {
        kunci: 'transferNumber',
        judul: 'Nomor',
        render: (b) => (
          <div className="flex items-center gap-2">
            <span className="font-medium text-netral-800">{b.transferNumber}</span>
            {belumCocok(b) ? <Badge nada="peringatan">Belum cocok</Badge> : null}
          </div>
        ),
      },
      { kunci: 'transferDate', judul: 'Tanggal', render: (b) => formatTanggal(b.transferDate) },
      {
        kunci: 'sourceUnit',
        judul: 'Dari Unit',
        render: (b) => b.sourceUnit?.name ?? b.sourceUnitId,
      },
      {
        kunci: 'destinationUnit',
        judul: 'Ke Unit',
        render: (b) => b.destinationUnit?.name ?? b.destinationUnitId,
      },
      { kunci: 'amount', judul: 'Jumlah', rata: 'kanan', render: (b) => formatRupiah(b.amount) },
      {
        kunci: 'varianceAmount',
        judul: 'Selisih',
        rata: 'kanan',
        render: (b) => (
          <span className={b.varianceAmount !== 0 ? 'font-medium text-red-700' : ''}>
            {formatRupiah(b.varianceAmount)}
          </span>
        ),
      },
      { kunci: 'status', judul: 'Status', render: (b) => <BadgeStatus status={b.status} /> },
      {
        kunci: 'aksi',
        judul: 'Aksi',
        sortable: false,
        render: (b) => (
          <div className="flex flex-wrap gap-1.5">
            {punyaIzin('transfer:approve') && b.status === 'REQUESTED' ? (
              <Button ukuran="kecil" onClick={() => setKonfirmasi({ tindakan: 'approve', data: b })}>
                Setujui
              </Button>
            ) : null}
            {punyaIzin('transfer:create') && b.status === 'APPROVED' ? (
              <Button ukuran="kecil" varian="garis" onClick={() => setKonfirmasi({ tindakan: 'send', data: b })}>
                Kirim
              </Button>
            ) : null}
            {punyaIzin('transfer:receive') && b.status === 'SENT' ? (
              <Button ukuran="kecil" varian="sekunder" onClick={() => setKonfirmasi({ tindakan: 'receive', data: b })}>
                Terima
              </Button>
            ) : null}
            {punyaIzin('transfer:match') && (b.status === 'RECEIVED' || b.status === 'NEEDS_REVIEW') ? (
              <Button ukuran="kecil" varian="utama" onClick={() => setKonfirmasi({ tindakan: 'match', data: b })}>
                Cocokkan
              </Button>
            ) : null}
          </div>
        ),
      },
    ],
    [punyaIzin],
  )

  return (
    <div>
      <PageHeader
        judul="Transfer Antarunit"
        deskripsi="Permintaan transfer dana antar unit dengan pelacakan Kirim → Terima → Cocokkan. Baris yang belum cocok disorot."
        ekspor={{
          path: '/reports/interunit-transfers',
          namaDasar: 'transfer-antarunit',
          query: { dari: filter.dari || undefined, sampai: filter.sampai || undefined },
        }}
        aksi={punyaIzin('transfer:create') ? <Button onClick={() => setModal(true)}>+ Permintaan Transfer</Button> : null}
      />

      <FilterBar
        nilai={filter}
        onUbah={setFilter}
        unit={opsiUnit}
        tampilkan={{ unitId: true, dari: true, sampai: true, fundId: false, program: false, kategori: false }}
        ekstra={
          <Select
            label="Status"
            placeholder="Semua status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            opsi={['REQUESTED', 'APPROVED', 'SENT', 'RECEIVED', 'MATCHED', 'FAILED', 'CANCELLED', 'NEEDS_REVIEW'].map((s) => ({
              value: s,
              label: s,
            }))}
            className="min-w-40"
          />
        }
      />

      <DataTable
        kolom={kolom}
        data={daftar.items}
        kunciBaris={(b) => b.id}
        memuat={daftar.memuat}
        galat={daftar.galat}
        kodeGalat={daftar.kodeGalat}
        onCoba={daftar.muat}
        meta={daftar.meta}
        onHalaman={daftar.setHalaman}
        kelasBaris={(b) => (belumCocok(b) ? 'bg-emas-100/40' : '')}
        pesanKosong="Belum ada transfer antarunit."
      />

      <FormTransfer terbuka={modal} onTutup={() => setModal(false)} setelah={daftar.muat} />

      <ConfirmDialog
        terbuka={konfirmasi !== null}
        onTutup={() => setKonfirmasi(null)}
        judul={`Konfirmasi ${konfirmasi?.tindakan ?? ''}`}
        pesan={`Lanjutkan tindakan "${konfirmasi?.tindakan}" untuk transfer ${konfirmasi?.data.transferNumber} sebesar ${formatRupiah(konfirmasi?.data.amount ?? 0)}?`}
        labelKonfirmasi="Lanjutkan"
        berbahaya={false}
        memuat={aksi.memuat}
        onKonfirmasi={() => {
          if (!konfirmasi) return
          void jalankan(konfirmasi.data.id, konfirmasi.tindakan)
        }}
      />
    </div>
  )
}

function FormTransfer({
  terbuka,
  onTutup,
  setelah,
}: {
  terbuka: boolean
  onTutup: () => void
  setelah: () => void
}) {
  const opsiUnit = useOpsiUnit()
  const akunKas = useApi<unknown>(terbuka ? '/cash-accounts' : null, { perPage: 300 })
  const daftarKas: AkunKas[] = Array.isArray(akunKas.data)
    ? (akunKas.data as AkunKas[])
    : ((akunKas.data as { items?: AkunKas[] } | null)?.items ?? [])

  const [sourceUnitId, setSourceUnitId] = useState('')
  const [destinationUnitId, setDestinationUnitId] = useState('')
  const [sourceCashAccountId, setSourceCashAccountId] = useState('')
  const [destinationCashAccountId, setDestinationCashAccountId] = useState('')
  const [transferDate, setTransferDate] = useState(new Date().toISOString().slice(0, 10))
  const [amount, setAmount] = useState('')
  const [purpose, setPurpose] = useState('')
  const [matchingReference, setMatchingReference] = useState('')
  const [notes, setNotes] = useState('')
  const [galat, setGalat] = useState<string | null>(null)
  const [memuat, setMemuat] = useState(false)

  const simpan = async () => {
    setGalat(null)
    if (!sourceUnitId || !destinationUnitId) return setGalat('Unit asal dan tujuan wajib dipilih.')
    if (sourceUnitId === destinationUnitId) return setGalat('Unit asal dan tujuan tidak boleh sama.')
    if (!sourceCashAccountId || !destinationCashAccountId) return setGalat('Akun kas sumber dan tujuan wajib dipilih.')
    const nilai = Number(amount.replace(/[^0-9.-]/g, ''))
    if (!nilai || nilai <= 0) return setGalat('Jumlah transfer harus lebih dari nol.')

    setMemuat(true)
    try {
      await api.post(
        '/interunit-transfers',
        {
          sourceUnitId,
          destinationUnitId,
          sourceCashAccountId,
          destinationCashAccountId,
          amount: nilai,
          transferDate,
          purpose: purpose.trim() || undefined,
          matchingReference: matchingReference.trim() || undefined,
          notes: notes.trim() || undefined,
        },
        { idempotencyKey: buatIdempotencyKey() },
      )
      setelah()
      onTutup()
    } catch (e) {
      setGalat(pesanKesalahan(e))
    } finally {
      setMemuat(false)
    }
  }

  const kasSumber = daftarKas.filter((k) => !sourceUnitId || k.unitId === sourceUnitId)
  const kasTujuan = daftarKas.filter((k) => !destinationUnitId || k.unitId === destinationUnitId)

  return (
    <Modal
      terbuka={terbuka}
      onTutup={onTutup}
      judul="Permintaan Transfer Antarunit"
      lebar="max-w-3xl"
      footer={
        <>
          <Button varian="garis" onClick={onTutup} disabled={memuat}>
            Batal
          </Button>
          <Button onClick={() => void simpan()} memuat={memuat}>
            Kirim Permintaan
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {galat ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {galat}
          </div>
        ) : null}
        <KolomForm kolom={2}>
          <Select
            label="Unit Asal"
            placeholder="Pilih unit asal"
            opsi={opsiUnit}
            value={sourceUnitId}
            onChange={(e) => setSourceUnitId(e.target.value)}
            required
          />
          <Select
            label="Unit Tujuan"
            placeholder="Pilih unit tujuan"
            opsi={opsiUnit}
            value={destinationUnitId}
            onChange={(e) => setDestinationUnitId(e.target.value)}
            required
          />
          <Select
            label="Kas/Bank Sumber"
            placeholder="Pilih akun sumber"
            opsi={kasSumber.map((k) => ({ value: k.id, label: `${k.code} — ${k.name}` }))}
            value={sourceCashAccountId}
            onChange={(e) => setSourceCashAccountId(e.target.value)}
            required
          />
          <Select
            label="Kas/Bank Tujuan"
            placeholder="Pilih akun tujuan"
            opsi={kasTujuan.map((k) => ({ value: k.id, label: `${k.code} — ${k.name}` }))}
            value={destinationCashAccountId}
            onChange={(e) => setDestinationCashAccountId(e.target.value)}
            required
          />
          <Input
            type="date"
            label="Tanggal Transfer"
            value={transferDate}
            onChange={(e) => setTransferDate(e.target.value)}
            required
          />
          <Input
            label="Jumlah (Rp)"
            inputMode="numeric"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
          />
          <Input
            label="Referensi Pencocokan"
            value={matchingReference}
            onChange={(e) => setMatchingReference(e.target.value)}
            bantuan="Dipakai saat mencocokkan transfer dengan mutasi bank."
          />
          <Input label="Keperluan" value={purpose} onChange={(e) => setPurpose(e.target.value)} />
        </KolomForm>
        <Textarea label="Catatan" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
      </div>
    </Modal>
  )
}
